import { useEffect, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { pushedBy, withMark, writeParam } from "@/lib/urlOverlay";

/** An overlay the URL holds (BRK-S347, BRK-S357): its param, how the URL's
 *  value is read (null when it opens nothing), and the mark its entries
 *  carry. */
export interface UrlOverlay {
  param: string;
  read: (params: URLSearchParams) => string | null;
  mark: string;
}

/**
 * An overlay's value, from the URL: the quote popup's card (BRK-S347) and
 * Preview submission (BRK-S357).
 * - `open` pushes the value, so Back closes the overlay.
 * - `close` goes back to the entry it pushed, or, for a link that opened the
 *   overlay directly, replaces the URL without it. A second close before the
 *   URL moves is ignored, so a double Escape never leaves the page.
 * - `closeThen` closes first and runs `then` once the overlay is gone, so a
 *   step that leaves the page (the profile gate) never races the Back.
 */
export function useUrlOverlay({ param, read, mark }: UrlOverlay) {
  const location = useLocation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const value = read(params);
  const closing = useRef(false);
  const after = useRef<(() => void) | null>(null);

  useEffect(() => {
    closing.current = false;
    if (value === null && after.current) {
      const then = after.current;
      after.current = null;
      then();
    }
  }, [location.key, value]);

  function open(next: string) {
    navigate(
      { pathname: location.pathname, search: writeParam(params, param, next) },
      { state: withMark(location.state, mark) },
    );
  }

  function close() {
    if (value === null || closing.current) return;
    closing.current = true;
    if (pushedBy(location.state, mark)) navigate(-1);
    else
      navigate(
        {
          pathname: location.pathname,
          search: writeParam(params, param, null),
        },
        { replace: true, state: location.state },
      );
  }

  function closeThen(then: () => void) {
    if (value === null) {
      then();
      return;
    }
    after.current = then;
    close();
  }

  return { value, open, close, closeThen };
}
